import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Container, Form, Row, Col } from "react-bootstrap";
import supabase from "../../supabase";
import { useNotification } from "../../context/NotificationContext";

const FormulaireEntreprise = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [departements, setDepartements] = useState([]);
  const { showNotification } = useNotification();

  const {
    selectedCategories = [],
    selectedDepartements = [], // 🔥 Maintenant ce sont des départements, pas des régions
    premiumOption = null,
    totalPrice = 0,
  } = location.state || {};

  useEffect(() => {
    const fetchDepartements = async () => {
      const { data, error } = await supabase
        .from("departements")
        .select("*")
        .order("numero");

      if (error) {
        console.error("Erreur chargement départements :", error.message);
      } else {
        setDepartements(data);
      }
    };

    fetchDepartements();
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    logo: "",
    tel: "",
    site: "",
    adresse: "",
    cp: null,
    ville: "",
    metier: selectedCategories.join(";"),
    message: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      showNotification("Les mots de passe ne correspondent pas.", "warning");
      return;
    }

    try {
      // Déterminer le nom du plan en fonction de l'option choisie
      let planName = "Basique"; // Plan de base pour les professionnels
      if (premiumOption === "partenaire") {
        planName = "Partenaire";
      } else if (premiumOption === "premium") {
        planName = "PartenairePremium";
      }

      // Données à stocker dans la table 'profiles' ou 'users' lors de l'inscription
      const userData = {
        account_type: "Artisan",
        plan: planName,
        full_name: formData.name, // Assurez-vous d'avoir une colonne pour le nom de l'entreprise
      };

      // 1. Créer l'utilisateur avec Supabase Auth
      // Supabase enverra automatiquement un e-mail de confirmation.
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: userData,
        },
      });

      if (signupError) {
        console.error("❌ Erreur création utilisateur:", signupError);
        showNotification(`Erreur lors de la création du compte : ${signupError.message}`, "danger");
        return;
      }
      
      if (!signupData.user) {
        showNotification("Un problème est survenu. L'utilisateur n'a pas été créé, veuillez réessayer.", "danger");
        return;
      }

      const userId = signupData.user.id;

      // 2. Forcer la mise à jour du profil pour garantir le type de compte et le plan
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          account_type: "Artisan",
          plan: planName,
        })
        .eq("id", userId);

      if (profileError) {
        console.error("Erreur mise à jour du profil:", profileError);
        showNotification("Erreur lors de la configuration de votre profil. Veuillez contacter le support.", "danger");
        return;
      }

      // Les informations de l'entreprise (hors profil)
      // On ne retire que les champs qui ne sont pas dans la table 'artisans'
      const { message, password, confirmPassword, ...artisanFields } = formData;

      // 3. Insérer les détails de l'entreprise dans la table 'artisans'
      const { data: artisanData, error: artisanError } = await supabase
        .from("artisans")
        .insert([
          {
            ...artisanFields,
            profile_id: userId,
          },
        ])
        .select()
        .single();

      if (artisanError || !artisanData?.id) {
        console.error("Erreur Enregistrement Artisan:", artisanError);
        // À ce stade, l'utilisateur est créé mais pas son profil artisan.
        // Il faudrait prévoir une logique de compensation ou informer l'utilisateur.
        showNotification("Votre compte utilisateur a été créé, mais une erreur est survenue lors de la création de votre profil d'entreprise. Veuillez contacter le support.", "danger");
        return;
      }

      const artisanId = artisanData.id;

      // 3. Insérer les départements choisis
      const departementInserts = selectedDepartements.map((departementId) => ({
        artisan_id: artisanId,
        departement_id: departementId,
      }));

      const { error: departementError } = await supabase
        .from("artisan_departements")
        .insert(departementInserts);

      if (departementError) {
        console.error("Erreur liaison artisan-départements:", departementError);
        showNotification("Erreur lors de l'association des départements. Veuillez contacter le support.", "danger");
        return;
      }

      // 4. Gérer les cas Premium ou Paiement
      if (premiumOption === "premium") {
         await fetch(`${process.env.REACT_APP_SERVER_URL}/email/contact-request`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject: "Demande d'inscription PREMIUM",
            from: formData.email,
            name: formData.name,
            message: formData.message,
            tel: formData.tel,
            site: formData.site,
            metiers: selectedCategories,
            departements: selectedDepartements,
          }),
        });
      
        showNotification("Votre demande a été envoyée. Veuillez également vérifier votre boîte mail pour confirmer votre compte.", "success");
        navigate("/"); // ou vers une page de succès
        return;
      }

      // Si un paiement est requis, rediriger vers la page de paiement
      if (totalPrice > 0) {
        navigate("/paiement-artisan", {
          state: { totalPrice, email: formData.email },
        });
      } else {
        // Si l'inscription est gratuite, informer de la confirmation par e-mail
        showNotification("Inscription réussie ! Veuillez consulter votre boîte de réception pour confirmer votre adresse e-mail et activer votre compte.", "success");
        navigate("/login"); // ou une page de confirmation
      }

    } catch (err) {
      console.error("❌ Erreur générale:", err);
      showNotification("Une erreur est survenue lors de l'inscription.", "danger");
    }
  };

  return (
    <Container className="mt-5">
      <h2 className="text-center mb-4">Informations de votre entreprise</h2>

      <div className="mb-4 p-3 border rounded bg-light">
        <h5 className="mb-3">Résumé de votre sélection :</h5>
        {selectedCategories.length > 0 && (
          <p>
            <strong>Métier(s) :</strong> {selectedCategories.join("; ")}
          </p>
        )}
        {selectedDepartements.length > 0 && (
          <p>
            <strong>Département(s) :</strong>{" "}
            {selectedDepartements
              .map((deptId) => {
                const dept = departements.find((d) => d.id === deptId);
                return dept ? `${dept.numero} - ${dept.nom}` : deptId;
              })
              .join(", ")}
          </p>
        )}
        {premiumOption && (
          <p>
            <strong>Option :</strong>{" "}
            {premiumOption === "partenaire" ? "Partenaire" : ""}
            {premiumOption === "premium" ? "Partenaire PREMIUM" : ""}
          </p>
        )}
        <p>
          <strong>Prix total :</strong> {premiumOption === "premium" ? "Sur devis" : totalPrice + "€"} 
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group controlId="formName" className="mb-3">
              <Form.Label>Nom de l'entreprise *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Ex: Menuiserie Dupont"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="formEmail" className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="exemple@entreprise.fr"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId="formPassword">
              <Form.Label>Mot de passe *</Form.Label>
              <Form.Control
                type="password"
                name="password"
                placeholder="Choisissez un mot de passe sécurisé"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formConfirmPassword">
              <Form.Label>Confirmez le mot de passe *</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                placeholder="Retapez votre mot de passe"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId="formTel" className="mb-3">
              <Form.Label>Téléphone</Form.Label>
              <Form.Control
                type="text"
                name="tel"
                placeholder="06 01 02 03 04"
                value={formData.tel}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="formSite" className="mb-3">
              <Form.Label>Site internet</Form.Label>
              <Form.Control
                type="text"
                name="site"
                placeholder="https://mon-entreprise.fr"
                value={formData.site}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group controlId="formAdresse" className="mb-3">
          <Form.Label>Adresse</Form.Label>
          <Form.Control
            type="text"
            name="adresse"
            placeholder="12 rue des artisans"
            value={formData.adresse}
            onChange={handleChange}
          />
        </Form.Group>

        <Row>
          <Col md={4}>
            <Form.Group controlId="formCP" className="mb-3">
              <Form.Label>Code Postal</Form.Label>
              <Form.Control
                type="number"
                name="cp"
                placeholder="75000"
                value={formData.cp}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>

          <Col md={8}>
            <Form.Group controlId="formVille" className="mb-3">
              <Form.Label>Ville</Form.Label>
              <Form.Control
                type="text"
                name="ville"
                placeholder="Paris"
                value={formData.ville}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group controlId="formMetier" className="mb-3">
          <Form.Control
            type="text"
            name="metier"
            value={formData.metier}
            hidden
          />
        </Form.Group>

        <Form.Group controlId="formLogo" className="mb-4">
          <Form.Label>Logo (URL)</Form.Label>
          <Form.Control
            type="text"
            name="logo"
            placeholder="Lien de votre logo (optionnel)"
            value={formData.logo}
            onChange={handleChange}
          />
        </Form.Group>

        {premiumOption === "premium" && (
          <Form.Group controlId="formMessage" className="mb-4">
            <Form.Label>Votre message</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="message"
              placeholder="Décrivez vos besoins ou posez vos questions..."
              value={formData.message}
              onChange={handleChange}
            />
          </Form.Group>
        )}

        <div className="text-center">
          <Button variant="primary" type="submit" size="lg">
            Valider et créer mon compte
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default FormulaireEntreprise;
